import React, { useEffect, useMemo, useState } from "react";
import {
  alpha,
  Avatar,
  Box,
  Container,
  Fade,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Person,
  Security,
  AccountBalanceWallet,
  LoginOutlined,
  Settings,
  ChevronRight,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import PersonalInfoSection from "../../components/profile/PersonalInfoSection";
import AccountSettingsSection from "../../components/profile/AccountSettingsSection";
import SecurityOverviewSection from "../../components/profile/SecurityOverviewSection";
import FinancialPreferencesSection from "../../components/profile/FinancialPreferencesSection";
import { PageContainer } from "../../components/common/StyledComponents";
import SurfaceCard from "../../components/common/ui/SurfaceCard";
import PrimaryButton from "../../components/common/ui/PrimaryButton";
import { useAuth } from "../../auth/AuthContext";
import { LOGIN_ROUTE_PATH, PROFILE_TAB_QUERY_PARAM, PROFILE_TAB_PERSONAL, PROFILE_TAB_SECURITY, PROFILE_TAB_FINANCIAL, PROFILE_TAB_SETTINGS } from "../../utils/constants/AppConstants";

const TAB_KEYS = [
  PROFILE_TAB_PERSONAL,
  PROFILE_TAB_SETTINGS,
  PROFILE_TAB_SECURITY,
  PROFILE_TAB_FINANCIAL,
];

const UserProfilePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get(PROFILE_TAB_QUERY_PARAM) || "";
  const keyFromIndex = (index: number): string => {
    return TAB_KEYS[index] || PROFILE_TAB_PERSONAL;
  };
  const initialIndex = useMemo(() => {
    const indexFromKey = (key: string): number => {
      const idx = TAB_KEYS.indexOf(key);
      return idx >= 0 ? idx : 0;
    };

    return indexFromKey(tabParam);
  }, [tabParam]);
  const [activeTab, setActiveTab] = useState(initialIndex);
  const { isAuthenticated } = useAuth();

  const profileSections = [
    {
      key: PROFILE_TAB_PERSONAL,
      title: "Personal Information",
      description: "Name and identity details",
      icon: <Person />,
      color: theme.palette.primary.main,
      content: <PersonalInfoSection />,
    },
    {
      key: PROFILE_TAB_SETTINGS,
      title: "Account Settings",
      description: "Preferences and account controls",
      icon: <Settings />,
      color: theme.palette.secondary.main,
      content: <AccountSettingsSection />,
    },
    {
      key: PROFILE_TAB_SECURITY,
      title: "Security",
      description: "Password and email protection",
      icon: <Security />,
      color: theme.palette.warning.main,
      content: <SecurityOverviewSection />,
    },
    {
      key: PROFILE_TAB_FINANCIAL,
      title: "Financial Preferences",
      description: "Currency and finance defaults",
      icon: <AccountBalanceWallet />,
      color: theme.palette.success.main,
      content: <FinancialPreferencesSection />,
    },
  ];

  const handleSectionChange = (newValue: number) => {
    setActiveTab(newValue);
    const newParams = new URLSearchParams(searchParams);
    newParams.set(PROFILE_TAB_QUERY_PARAM, keyFromIndex(newValue));
    setSearchParams(newParams, { replace: true });
  };

  const activeSection = profileSections[activeTab] || profileSections[0];

  useEffect(() => {
    // Sync tab when URL query changes externally
    setActiveTab(initialIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIndex]);

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 7 } }}>
          <SurfaceCard
            sx={{
              p: { xs: 3, md: 5 },
              textAlign: "center",
              borderRadius: "18px",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
              background: `linear-gradient(160deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
            }}
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                mx: "auto",
                mb: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.22),
                color: theme.palette.primary.light,
              }}
            >
              <Person />
            </Avatar>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              Profile Settings
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mx: "auto", mb: 4 }}>
              Manage your account information, preferences, and security settings. Please sign in to access your profile.
            </Typography>
            <PrimaryButton
              startIcon={<LoginOutlined />}
              onClick={() => navigate(LOGIN_ROUTE_PATH)}
              sx={{
                px: 4,
                py: 1.5,
                color: theme.palette.primary.contrastText,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              }}
            >
              Sign In to Continue
            </PrimaryButton>
          </SurfaceCard>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="xl" sx={{ py: { xs: 2.5, md: 4 } }}>
        <Fade in={true} timeout={600}>
          <Box>
            <SurfaceCard
              sx={{
                mb: 3,
                p: { xs: 2.2, md: 3.2 },
                borderRadius: "16px",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.background.paper, 0.45)} 100%)`,
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Stack direction="row" spacing={1.4} alignItems="center">
                  <Avatar sx={{ width: 42, height: 42, bgcolor: alpha(theme.palette.primary.main, 0.24), color: theme.palette.primary.light }}>
                    <Person />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      Account & Profile
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Keep your identity, security, and financial preferences up to date.
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </SurfaceCard>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4} lg={3.5}>
                <SurfaceCard
                  sx={{
                    p: 1.4,
                    borderRadius: "14px",
                    position: { md: "sticky" },
                    top: { md: 92 },
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                  }}
                >
                  <Stack spacing={1}>
                    {profileSections.map((section, index) => {
                      const selected = activeTab === index;

                      return (
                        <Box
                          key={section.key}
                          onClick={() => handleSectionChange(index)}
                          sx={{
                            p: 1.25,
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: 1.2,
                            cursor: "pointer",
                            border: `1px solid ${selected ? alpha(section.color, 0.48) : alpha(theme.palette.text.primary, 0.08)}`,
                            bgcolor: selected ? alpha(section.color, 0.15) : alpha(theme.palette.background.default, 0.24),
                            transition: "all 0.18s ease",
                            "&:hover": {
                              borderColor: alpha(section.color, 0.38),
                              bgcolor: alpha(section.color, 0.1),
                            },
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 34,
                              height: 34,
                              bgcolor: alpha(section.color, 0.2),
                              color: section.color,
                            }}
                          >
                            {section.icon}
                          </Avatar>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: selected ? 700 : 600 }}>
                              {section.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {section.description}
                            </Typography>
                          </Box>
                          <ChevronRight sx={{ color: selected ? section.color : theme.palette.text.disabled }} />
                        </Box>
                      );
                    })}
                  </Stack>
                </SurfaceCard>
              </Grid>

              <Grid item xs={12} md={8} lg={8.5}>
                <SurfaceCard
                  sx={{
                    borderRadius: "14px",
                    overflow: "hidden",
                    border: `1px solid ${alpha(activeSection.color, 0.34)}`,
                  }}
                >
                  <Box
                    sx={{
                      p: { xs: 2, md: 2.4 },
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      background: `linear-gradient(135deg, ${alpha(activeSection.color, 0.18)} 0%, ${alpha(theme.palette.background.paper, 0.4)} 100%)`,
                    }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(activeSection.color, 0.24), color: activeSection.color }}>
                        {activeSection.icon}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {activeSection.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {activeSection.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <Fade in={true} timeout={260} key={activeSection.key}>
                    <Box sx={{ p: { xs: 0.5, md: 1 } }}>{activeSection.content}</Box>
                  </Fade>
                </SurfaceCard>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      </Container>
    </PageContainer>
  );
};

export default UserProfilePage;
